/**
 * Sprint T-15 · Founder Security Center · Overview tab.
 *
 * Read-only consolidation of T-14 endpoints. Pure rendering, no business logic.
 */
import React from "react";
import { ShieldCheck, Key, AlertTriangle, Clock, Activity, ShieldAlert } from "lucide-react";
import { WidgetCard, WidgetGrid, MetricCard, CitationBadge, EmptyState, LoadingState } from "../advisor";
import { useSecurityOverview } from "../../hooks/useSecurity";

const fmtIso = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function SecurityOverviewTab() {
  const { tfa, apikeys, lastLogin, failuresRecent, rateLimits, recent } = useSecurityOverview();

  const keysActive = (apikeys.data?.items || []).filter((k) => k.active && !k.revoked).length;
  const lastLoginItem = lastLogin.data?.items?.[0];
  const failuresCount = failuresRecent.data?.total ?? failuresRecent.data?.items?.length ?? 0;
  const rateLimitCount = rateLimits.data?.total ?? rateLimits.data?.items?.length ?? 0;

  // System health heuristic · 100% backend-driven (no client recomputation)
  const tfaEnabled = !!tfa.data?.enabled;
  const tfaMandatory = !!tfa.data?.is_mandatory_for_role;
  const tfaOk = tfaEnabled || !tfaMandatory;
  const failuresOk = failuresCount < 5;
  const rateOk = rateLimitCount < 10;
  const overall = tfaOk && failuresOk && rateOk ? "ok" : (failuresCount > 20 || rateLimitCount > 50 ? "critical" : "warning");
  const overallLabel = { ok: "OK", warning: "Atención", critical: "Crítico" }[overall];
  const overallTone = { ok: "bg-emerald-500/15 text-emerald-500", warning: "bg-yellow-500/15 text-yellow-600", critical: "bg-red-500/15 text-red-500" }[overall];

  return (
    <section className="space-y-4" data-testid="security-overview-tab">
      <WidgetCard
        title="Estado general del sistema"
        description="Consolidación de señales backend AG-T14 · cero cálculos en frontend"
        citations={["security.access_control", "audit.AuditAction"]}
        testId="widget-security-overall"
      >
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${overallTone}`} data-testid="security-overall-status">
            <ShieldCheck className="h-3.5 w-3.5" />
            {overallLabel}
          </span>
          <span className="text-xs text-[var(--ag-text-dim)]">
            2FA {tfaEnabled ? "activo" : (tfaMandatory ? "REQUERIDO" : "inactivo")} · {keysActive} keys activas · {failuresCount} fallos recientes · {rateLimitCount} rate-limits
          </span>
        </div>
      </WidgetCard>

      <WidgetGrid cols="lg:grid-cols-4">
        <WidgetCard title="Estado 2FA" citations={["/api/auth/2fa/status"]} testId="widget-overview-2fa">
          {tfa.isLoading && <LoadingState />}
          {tfa.error && <EmptyState message="Sin datos." />}
          {tfa.data && (
            <div data-testid="overview-2fa-block">
              <div className="flex items-center gap-2">
                <ShieldCheck className={`h-5 w-5 ${tfaEnabled ? "text-emerald-500" : "text-[var(--ag-text-dim)]"}`} />
                <span className="text-2xl font-bold">{tfaEnabled ? "Activo" : "Inactivo"}</span>
              </div>
              <div className="mt-1 text-[11px] text-[var(--ag-text-dim)]">
                Rol: {tfa.data.role} · {tfaMandatory ? "obligatorio" : "opcional"} · {tfa.data.backup_codes_remaining} backup codes
              </div>
            </div>
          )}
        </WidgetCard>

        <WidgetCard title="API Keys activas" citations={["/api/security/api-keys/"]} testId="widget-overview-apikeys">
          {apikeys.isLoading && <LoadingState />}
          {apikeys.error && <EmptyState message="Sin datos." />}
          {apikeys.data && (
            <MetricCard
              label="Keys activas"
              value={keysActive}
              hint={`${(apikeys.data?.items || []).length} totales (incluye revocadas)`}
              testId="overview-apikeys-count"
            />
          )}
        </WidgetCard>

        <WidgetCard title="Último login" citations={["audit.auth.login.ok"]} testId="widget-overview-last-login">
          {lastLogin.isLoading && <LoadingState />}
          {lastLogin.error && <EmptyState message="Sin datos." />}
          {lastLogin.data && (
            lastLoginItem ? (
              <div data-testid="overview-last-login-block">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm font-medium">{lastLoginItem.actor_email || lastLoginItem.actor_id || "—"}</span>
                </div>
                <div className="mt-1 text-[11px] text-[var(--ag-text-dim)]">
                  {fmtIso(lastLoginItem.ts_iso)} · IP {lastLoginItem.ip || "—"}
                </div>
              </div>
            ) : <EmptyState message="Sin logins registrados." />
          )}
        </WidgetCard>

        <WidgetCard title="Rate limits activados" citations={["audit.ratelimit.triggered"]} testId="widget-overview-ratelimit">
          {rateLimits.isLoading && <LoadingState />}
          {rateLimits.error && <EmptyState message="Sin datos." />}
          {rateLimits.data && (
            <MetricCard
              label="Disparos ratelimit"
              value={rateLimitCount}
              hint="Total registrado (sin filtro temporal · backend retiene 365d)"
              testId="overview-ratelimit-count"
            />
          )}
        </WidgetCard>
      </WidgetGrid>

      <WidgetCard
        title="Eventos críticos recientes"
        description="auditoría · ok=false (cualquier acción fallida)"
        citations={["/api/superadmin/audit-events?only_failures=true"]}
        testId="widget-overview-failures"
      >
        {failuresRecent.isLoading && <LoadingState />}
        {failuresRecent.error && <EmptyState message="Sin datos." />}
        {failuresRecent.data && (
          (failuresRecent.data.items || []).length === 0 ? (
            <EmptyState message="Sin eventos críticos." testId="overview-failures-empty" />
          ) : (
            <ul className="space-y-1" data-testid="overview-failures-list">
              {(failuresRecent.data.items || []).slice(0, 5).map((e, i) => (
                <li
                  key={`${e.ts_iso}-${i}`}
                  data-testid={`overview-failure-${i}`}
                  className="flex items-center justify-between rounded border border-red-500/30 bg-red-500/5 px-2 py-1.5 text-xs"
                >
                  <span className="flex items-center gap-2">
                    <ShieldAlert className="h-3.5 w-3.5 text-red-500" />
                    <span className="font-mono">{e.action}</span>
                    <span className="text-[var(--ag-text-dim)]">{e.actor_email || "anon"}</span>
                  </span>
                  <span className="text-[10px] text-[var(--ag-text-dim)]">{fmtIso(e.ts_iso)}</span>
                </li>
              ))}
            </ul>
          )
        )}
      </WidgetCard>

      <WidgetCard
        title="Actividad reciente · últimos 5 eventos"
        description="audit.AuditAction · sin filtro"
        citations={["/api/superadmin/audit-events?limit=5"]}
        testId="widget-overview-recent"
      >
        {recent.isLoading && <LoadingState />}
        {recent.error && <EmptyState message="Sin datos." />}
        {recent.data && (
          (recent.data.items || []).length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="space-y-1" data-testid="overview-recent-list">
              {(recent.data.items || []).slice(0, 5).map((e, i) => (
                <li
                  key={`${e.ts_iso}-${i}`}
                  data-testid={`overview-recent-${i}`}
                  className="flex items-center justify-between rounded border border-[var(--ag-border)] px-2 py-1.5 text-xs"
                >
                  <span className="flex items-center gap-2">
                    <Activity className="h-3.5 w-3.5 text-[var(--ag-text-dim)]" />
                    <span className="font-mono">{e.action}</span>
                    <span className="text-[var(--ag-text-dim)]">{e.actor_email || "—"}</span>
                  </span>
                  <span className="text-[10px] text-[var(--ag-text-dim)]">{fmtIso(e.ts_iso)}</span>
                </li>
              ))}
            </ul>
          )
        )}
      </WidgetCard>

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["T-14 endpoints", "audit_events 365d retention", "ai_platform_freeze=true"]} />
      </footer>
    </section>
  );
}
